import { useIntl } from 'react-intl-next';

import { type UserConvoAIStreamingConfig } from '@atlassian/ai-agent-streaming';
import type { AiFeatureRequestEnvelopeSuggestIssuesDocumentationRequest } from '@atlassian/convo-ai-api';

import { fg } from '@confluence/feature-gating';
import { useIssueCreateSidePanelContext } from '../providers/IssueCreateSidePanelContextProvider';
import { useBulkCreateContext } from '../providers/BulkCreateContextProvider';

const getSelectedContent = (
	isCreatedFromActionItems: boolean,
	isCreatedFromTable: boolean,
	selectedActionItems: HTMLElement | undefined,
	selectedTable: HTMLTableElement | undefined,
) => {
	if (isCreatedFromActionItems) return selectedActionItems;
	if (isCreatedFromTable) return selectedTable;
	return null;
};

export const useBuildIssueSuggestionsRequest = (
	markdownPlusContent: string,
	pageAri: string,
	isConvoAIMigrationEnabled: boolean,
	overrideXProduct?: string,
): [AiFeatureRequestEnvelopeSuggestIssuesDocumentationRequest, UserConvoAIStreamingConfig] => {
	const { locale } = useIntl();
	const [{ isCreatedFromActionItems, isCreatedFromTable, expectedIssueCount }] =
		useBulkCreateContext();
	const [{ selectedActionItems, selectedTable }] = useIssueCreateSidePanelContext();

	const isUserSelectedCreation =
		(isCreatedFromActionItems || isCreatedFromTable) &&
		fg('confluence-create-work-items-from-action-items');
	const selectedContent = getSelectedContent(
		isCreatedFromActionItems,
		isCreatedFromTable,
		selectedActionItems,
		selectedTable,
	);

	return [
		{
			ai_feature_input: {
				context: {
					primary_text: isUserSelectedCreation ? ' ' : markdownPlusContent,
					...(isUserSelectedCreation ? { highlighted_text: selectedContent?.innerHTML } : {}),
					input_mime_type: 'text/markdown',
					source_ari: pageAri,
				},
				source: 'CONFLUENCE',
				locale,
				suggested_issues_config: {
					max_issues: isUserSelectedCreation ? expectedIssueCount : 10,
					suggested_issue_field_types: [
						{
							issue_type: 'Task',
							fields: [
								{
									field_name: 'Summary',
									field_type: 'Short-Text',
								},
								{
									field_name: 'Description',
									field_type: 'Paragraph',
								},
							],
						},
					],
				},
				sourceType: 'string',
			},
		},
		{
			apiPath: 'v2/ai-feature/jira/issue/source-type/documentation/suggestions/stream',
			enablePartialJson: false,
			skip: !markdownPlusContent || !isConvoAIMigrationEnabled,
			headers: {
				'x-experience-id': 'ai-issue-create-confluence-bulk',
				'x-product':
					overrideXProduct && fg('confluence-create-work-items-from-action-items')
						? overrideXProduct
						: 'confluence',
			},
		},
	];
};
